/**
 * Étape 3 de l'import : ce que le serveur a rapproché, validé par une personne.
 *
 *   Contrôle de lecture  toutes les lignes d'élèves du document ont-elles été
 *                 lues ? (ControleLecture, calculé à la lecture du fichier)
 *   Bilan         deux égalités qui doivent tomber juste, comme un
 *                 rapprochement bancaire : nos élèves d'un côté, les lignes du
 *                 document de l'autre. Rien ne se perd en route.
 *   À confirmer   ressemblance partielle, homonymes, date contradictoire : rien
 *                 n'est retenu tant qu'on n'a pas coché.
 *   Trouvés       nom identique ou matricule national connu : retenus d'office,
 *                 sauf si l'élève a déjà un autre résultat (cocher pour le
 *                 remplacer).
 *   Déjà enregistrés  ce qu'un import précédent ou une saisie a déjà posé : ni
 *                 reproposé, ni réécrit, ni déclaré ajourné. Réimporter la
 *                 même page, ou la page suivante d'une liste, ne touche à rien
 *                 de ce qui est fait.
 *   Pistes à examiner  pointage par paires (parti B, 01/11/2026) : notre élève
 *                 face à la ligne qui lui ressemble le plus, une seule fois
 *                 par paire - « C'est lui » ou « Ce n'est pas lui ».
 *   Sans piste    deux listes côte à côte : nos élèves (Ajourné / Absent, un
 *                 par un ou d'un coup si la liste est complète) et les élèves
 *                 de la liste (Candidat libre).
 *   Couleurs      chaque bloc reprend la couleur de son terme dans le bilan.
 *   Texte         en-têtes et pied de page du document, mis de côté, repliés.
 *
 * Rien n'est écrit avant « Enregistrer », et l'écriture - candidats libres
 * déclarés compris - tient en une transaction côté serveur.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { Loader2, ArrowLeft, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import ChampSelect from '../ui/ChampSelect';
import ConfirmDialog from '../ConfirmDialog';
import PiedActions from './PiedActions';
import ControleLecture from './ControleLecture';
import DeclarationCandidatLibre from './DeclarationCandidatLibre';
import { libelleMention } from './ResultatsExamen';

const LIBELLES_RESULTAT = { admis: 'Admis', ajourne: 'Ajourné', absent: 'Absent' };
const TEINTES_RESULTAT = {
  admis: 'bg-green-100 text-green-800',
  ajourne: 'bg-red-100 text-red-800',
  absent: 'bg-gray-200 text-gray-700'
};
const OPTIONS_RESULTAT = [
  { valeur: 'admis', libelle: 'Admis' },
  { valeur: 'ajourne', libelle: 'Ajourné' },
  { valeur: 'absent', libelle: 'Absent' }
];
const AUCUN = 'aucun';

// Les couleurs du bilan : vert acquis dans cet import, bleu ciel acquis avant,
// violet les candidats libres déclarés, ambre en attente, gris ce qui reste
// seul, gris clair ce qui ne compte pas.
const COULEURS = {
  trouve: 'bg-green-500',
  deja: 'bg-sky-500',
  libre: 'bg-violet-400',
  attente: 'bg-amber-400',
  seul: 'bg-gray-400',
  horsCompte: 'bg-gray-200'
};

// Le bandeau de chaque bloc reprend la couleur de son terme dans le bilan :
// la légende du haut sert de repère pour toute la page. Teintes douces en
// bandeau seulement - le bleu KLASYS reste celui des boutons.
const TONS = {
  attente: { bande: 'bg-amber-50 border-amber-200', titre: 'text-amber-900', puce: COULEURS.attente },
  trouve: { bande: 'bg-green-50 border-green-200', titre: 'text-green-900', puce: COULEURS.trouve },
  deja: { bande: 'bg-sky-50 border-sky-200', titre: 'text-sky-900', puce: COULEURS.deja },
  libre: { bande: 'bg-violet-50 border-violet-200', titre: 'text-violet-900', puce: COULEURS.libre },
  seul: { bande: 'bg-gray-50 border-gray-200', titre: 'text-gray-900', puce: COULEURS.seul },
  texte: { bande: 'bg-gray-50 border-gray-200', titre: 'text-gray-700', puce: COULEURS.horsCompte }
};

const BOUTON_PIED = 'h-9 min-w-[168px] justify-center gap-2';
const BOUTON_RELIER = 'h-8 flex-shrink-0 border-[#246990] text-[#246990] hover:bg-[#246990] hover:text-white';

const ClasseCase = 'h-4 w-4 cursor-pointer rounded border-gray-300 accent-[#246990]';
const Th = ({ children, className = '' }) => (
  <th className={`px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 ${className}`}>
    {children}
  </th>
);

/**
 * Le dossier d'inscription à l'examen, dans une colonne à lui : largeur fixe,
 * pour que le badge ne dépende pas de la longueur du nom (convention KLASYS).
 */
const BadgeDossier = ({ inscrit }) => (
  <span className={`inline-flex h-6 w-[110px] items-center justify-center rounded-lg text-[10px] font-semibold ${inscrit
    ? 'bg-green-50 text-green-800'
    : 'bg-gray-100 text-gray-600'
    }`}
  >
    {inscrit ? 'Avec dossier' : 'Sans dossier'}
  </span>
);

const BadgeResultat = ({ resultat }) => (resultat ? (
  <span className={`inline-flex h-6 w-[96px] items-center justify-center rounded-lg text-[10px] font-semibold ${TEINTES_RESULTAT[resultat]}`}>
    {LIBELLES_RESULTAT[resultat]}
  </span>
) : <span className="text-xs text-gray-400">-</span>);

const formaterDate = (iso) => {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : null;
};

const nomDe = (candidat) => (candidat ? `${candidat.nom || ''} ${candidat.prenom || ''}`.trim() : '');

/** Ce que dit la ligne du fichier : le nom tel qu'écrit, puis les précisions lues. */
const CelluleFichier = ({ fichier }) => {
  const precisions = [
    `Ligne ${fichier.ligne_fichier}`,
    fichier.matricule_national && `Mat. ${fichier.matricule_national}`,
    fichier.date_naissance && `Né(e) le ${formaterDate(fichier.date_naissance)}`
  ].filter(Boolean);
  return (
    <div className="min-w-0">
      <p className="text-sm font-medium text-gray-900">{fichier.nom_complet || '-'}</p>
      <p className="text-[11px] text-gray-500">{precisions.join(' - ')}</p>
    </div>
  );
};

const CelluleCandidat = ({ candidat }) => (
  <div className="min-w-0">
    <p className="text-sm font-medium text-gray-900">{nomDe(candidat)}</p>
    <p className="text-[11px] text-gray-500">
      {[candidat?.classe_nom, candidat?.date_naissance && `Né(e) le ${formaterDate(candidat.date_naissance)}`]
        .filter(Boolean).join(' - ')}
    </p>
  </div>
);

/**
 * La date de naissance de l'élève proposé, face à celle de la liste : c'est
 * le meilleur indice quand le nom diffère (« BAKANA Sonia » dans KLASYS,
 * « BAKALA NKOUNA Sonia Winner Abelinia » sur la liste, tous deux nés le
 * 16/10/2015).
 */
const ComparaisonDate = ({ fichier, candidat }) => {
  if (!candidat) return null;
  const date = formaterDate(candidat.date_naissance);
  if (!date) return <p className="mt-1 text-[11px] text-gray-500">Date de naissance inconnue dans KLASYS</p>;
  if (!fichier.date_naissance) return <p className="mt-1 text-[11px] text-gray-500">Né(e) le {date}</p>;
  const egale = candidat.date_naissance === fichier.date_naissance;
  return (
    <p className={`mt-1 text-[11px] ${egale ? 'text-[#246990]' : 'text-amber-700'}`}>
      Né(e) le {date} - {egale ? 'même date que la liste' : 'date différente de la liste'}
    </p>
  );
};

/** Un résultat déjà enregistré, différent de celui de la liste : à remplacer seulement si on coche. */
const Remplacement = ({ candidat, resultat, retenue }) => {
  if (!candidat?.resultat_actuel || !resultat || candidat.resultat_actuel === resultat) return null;
  const actuel = LIBELLES_RESULTAT[candidat.resultat_actuel];
  return (
    <p className="mt-1 text-[11px] text-amber-700">
      {retenue ? `remplacera ${actuel}` : `Déjà enregistré : ${actuel}. Cochez pour remplacer.`}
    </p>
  );
};

const Section = ({ titre, sousTitre, ton = 'seul', className = 'mb-4', children }) => {
  const t = TONS[ton] || TONS.seul;
  return (
    <Card className={`overflow-hidden ${className}`}>
      <div className={`border-b px-5 py-3 ${t.bande}`}>
        <p className={`flex items-center gap-2 text-sm font-semibold ${t.titre}`}>
          <span className={`inline-block h-2.5 w-2.5 flex-shrink-0 rounded-sm ${t.puce}`} />
          {titre}
        </p>
        {sousTitre && <p className="mt-0.5 text-xs text-gray-600">{sousTitre}</p>}
      </div>
      <CardContent className="p-0">{children}</CardContent>
    </Card>
  );
};

/**
 * Un côté du bilan : une barre, puis l'égalité écrite en toutes lettres. Les
 * termes vides restent écrits (« 0 élève sans inscrit ») : un zéro rassure
 * autant qu'un nombre.
 */
const Bilan = ({ titre, termes, total }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-4">
    <p className="mb-2 text-sm font-semibold text-gray-900">{titre}</p>
    <div className="flex h-2.5 overflow-hidden rounded bg-gray-100">
      {termes.filter((t) => t.valeur > 0).map((t) => (
        <div key={t.libelle} className={t.couleur} style={{ width: `${(t.valeur / Math.max(1, total)) * 100}%` }} />
      ))}
    </div>
    <p className="mt-2 text-xs leading-relaxed text-gray-600">
      {termes.map((t, i) => (
        <React.Fragment key={t.libelle}>
          {i > 0 && ' + '}
          <span className="whitespace-nowrap">
            <span className={`mr-1 inline-block h-2 w-2 rounded-sm align-middle ${t.couleur}`} />
            <span className="font-semibold text-gray-900 tabular-nums">{t.valeur}</span> {t.libelle}
          </span>
        </React.Fragment>
      ))}
      {' = '}
      <span className="font-semibold text-gray-900 tabular-nums">{total}</span>
    </p>
  </div>
);

/** Ajourné / Absent pour un élève sans ligne ; recliquer le choix actif l'efface. */
const MarqueSansLigne = ({ valeur, onChoisir, nom }) => (
  <div className="inline-flex flex-shrink-0 overflow-hidden rounded-lg border border-gray-200">
    {[
      { valeur: 'ajourne', libelle: 'Ajourné', actif: 'bg-red-100 text-red-800' },
      { valeur: 'absent', libelle: 'Absent', actif: 'bg-gray-200 text-gray-700' }
    ].map((choix, i) => (
      <button
        key={choix.valeur}
        type="button"
        onClick={() => onChoisir(valeur === choix.valeur ? null : choix.valeur)}
        aria-pressed={valeur === choix.valeur}
        aria-label={`${nom} : ${choix.libelle}`}
        className={`inline-flex h-8 w-[72px] items-center justify-center text-xs font-semibold transition-colors ${i > 0 ? 'border-l border-gray-200' : ''} ${valeur === choix.valeur ? choix.actif : 'bg-white text-gray-500 hover:bg-gray-50'}`}
      >
        {choix.libelle}
      </button>
    ))}
  </div>
);

// Absents du fichier, mention et matricule ne sont pas envoyés : un import
// sans colonne de mention ne retire pas une mention saisie à la main.
const depuisFichier = (base, resultat, fichier) => {
  const r = { ...base, resultat };
  if (fichier?.mention && resultat === 'admis') r.mention = fichier.mention;
  if (fichier?.matricule_national) r.matricule_national = fichier.matricule_national;
  if (fichier?.numero_table) r.numero_table = fichier.numero_table;
  return r;
};

const texteDeLigne = (ligne) => Object.entries(ligne)
  .filter(([cle, valeur]) => cle !== 'ligne' && valeur)
  .map(([, valeur]) => valeur)
  .join(' ');

const RapprochementResultats = ({
  analyse,
  typeListe,
  libelleLong,
  onRetour,
  onConfirmer,
  notifier,
  texteIgnore = [],
  controle = null
}) => {
  const parCle = useMemo(() => new Map(analyse.candidats.map((c) => [c.cle, c])), [analyse]);
  const pistesCandidats = useMemo(() => analyse.pistes_candidats || {}, [analyse]);
  const lignesSansEleve = useMemo(() => analyse.non_trouves.lignes || [], [analyse]);

  // Les lignes restées seules, par numéro : celles du détail et celles que
  // désignent les pistes de nos élèves.
  const fichiersSeuls = useMemo(() => new Map([
    ...lignesSansEleve.map((l) => [l.ligne, l.fichier]),
    ...Object.values(analyse.pistes_candidats || {}).map((p) => [p.ligne, p.fichier])
  ]), [analyse, lignesSansEleve]);

  // Une ligne dont l'élève a DÉJÀ ce résultat (import précédent, saisie à la
  // main) : rien à refaire. Elle n'est ni reproposée ni réécrite - réimporter
  // la même page ne demande pas de tout décocher.
  const dejaEnregistrees = useMemo(() => analyse.propositions.filter((p) => {
    const candidat = parCle.get(p.cle);
    return Boolean(candidat?.resultat_actuel && p.fichier.resultat && candidat.resultat_actuel === p.fichier.resultat);
  }), [analyse, parCle]);
  const lignesDeja = useMemo(() => new Set(dejaEnregistrees.map((p) => p.ligne)), [dejaEnregistrees]);
  const clesDeja = useMemo(() => new Set(dejaEnregistrees.map((p) => p.cle)), [dejaEnregistrees]);

  // Une décision par ligne proposée : retenue ou non, quel candidat, quel
  // résultat. Un « trouvé » n'est retenu d'office que si l'élève n'a encore
  // aucun résultat : un résultat déjà là ne se remplace que sur une coche.
  const [decisions, setDecisions] = useState(() => {
    const candidats = new Map(analyse.candidats.map((c) => [c.cle, c]));
    return Object.fromEntries(analyse.propositions.map((p) => [
      p.ligne,
      {
        retenue: p.statut === 'sur' && !candidats.get(p.cle)?.resultat_actuel,
        cle: p.cle,
        resultat: p.fichier.resultat || null
      }
    ]));
  });
  // Les liens faits à la main (« C'est lui ») : ligne restée seule -> élève.
  const [liens, setLiens] = useState({});
  // Les élèves de la liste déclarés candidats libres : ligne -> formulaire.
  const [nouveauxLibres, setNouveauxLibres] = useState({});
  const [declaration, setDeclaration] = useState(null);
  // Nos élèves sans ligne marqués à la main : cle -> 'ajourne' | 'absent'.
  const [marques, setMarques] = useState({});
  const [listeComplete, setListeComplete] = useState(false);
  const [marquesAuto, setMarquesAuto] = useState(() => new Set());
  const [voirTexte, setVoirTexte] = useState(false);
  const [voirDeja, setVoirDeja] = useState(false);
  const [confirmerToutRetenir, setConfirmerToutRetenir] = useState(false);
  // Les paires écartées par « Ce n'est pas lui » : « cle|ligne ».
  const [ecartees, setEcartees] = useState(() => new Set());
  const [confirmation, setConfirmation] = useState(false);
  const [enCours, setEnCours] = useState(false);

  const changer = (ligne, champs) => setDecisions((precedentes) => ({
    ...precedentes,
    [ligne]: { ...precedentes[ligne], ...champs }
  }));

  const aConfirmer = useMemo(
    () => analyse.propositions.filter((p) => p.statut === 'a_confirmer' && !lignesDeja.has(p.ligne)),
    [analyse, lignesDeja]
  );
  const trouves = useMemo(
    () => analyse.propositions.filter((p) => p.statut === 'sur' && !lignesDeja.has(p.ligne)),
    [analyse, lignesDeja]
  );

  const retenues = useMemo(
    () => analyse.propositions.filter((p) => !lignesDeja.has(p.ligne)
      && decisions[p.ligne]?.retenue && decisions[p.ligne]?.cle),
    [analyse, decisions, lignesDeja]
  );
  const liensListe = useMemo(
    () => Object.entries(liens).map(([ligne, lien]) => ({ ligne: Number(ligne), ...lien })),
    [liens]
  );
  const nouveauxListe = useMemo(
    () => Object.entries(nouveauxLibres).map(([ligne, n]) => ({ ligne: Number(ligne), ...n })),
    [nouveauxLibres]
  );

  // Les élèves attribués à une ligne dans cet import, retenue ou reliée à la main.
  const pris = useMemo(() => new Set([
    ...retenues.map((p) => decisions[p.ligne].cle),
    ...liensListe.map((l) => l.cle)
  ]), [retenues, decisions, liensListe]);

  // Un élève attribué deux fois : l'enregistrement le refuserait.
  const doublons = useMemo(() => {
    const vus = new Map();
    for (const cle of [...retenues.map((p) => decisions[p.ligne].cle), ...liensListe.map((l) => l.cle)]) {
      vus.set(cle, (vus.get(cle) || 0) + 1);
    }
    return new Set([...vus.entries()].filter(([, n]) => n > 1).map(([cle]) => cle));
  }, [retenues, decisions, liensListe]);

  // Les élèves proposés dans une ligne « à confirmer » que personne n'a encore
  // tranchée (ni cochée, ni écartée par « Aucun de nos candidats »). Ils ne
  // sont pas « sans ligne » : une ligne leur ressemble, on attend la
  // confirmation. Les y montrer était trompeur, et dangereux : une liste
  // déclarée complète les cochait ajournés d'office.
  const enAttente = useMemo(() => {
    const attente = new Set();
    for (const p of aConfirmer) {
      const d = decisions[p.ligne];
      if (d?.cle && !d.retenue && !pris.has(d.cle)) attente.add(d.cle);
    }
    return attente;
  }, [aConfirmer, decisions, pris]);

  // Nos élèves qui ont déjà un résultat et que cet import ne touche pas : sur
  // une ligne au même résultat, ou absents de ce fichier (une autre page de
  // la liste, une saisie à la main). Jamais « sans ligne », donc jamais
  // proposés ajournés : la page 11 ne doit pas défaire la page 10.
  const dejaConnus = useMemo(
    () => analyse.candidats.filter((c) => !pris.has(c.cle) && !enAttente.has(c.cle)
      && (clesDeja.has(c.cle) || c.resultat_actuel)),
    [analyse, pris, enAttente, clesDeja]
  );
  const dejaAbsents = useMemo(() => dejaConnus.filter((c) => !clesDeja.has(c.cle)), [dejaConnus, clesDeja]);

  // Le dossier d'examen (inscriptions_examens) : la fiche qui naît quand on
  // ouvre « Voir le dossier » dans la vue Dossiers. Il ne sert ici qu'à dire
  // qui « liste complète » marque ajourné d'office - un élève sans dossier n'a
  // peut-être pas été présenté. Il n'a de sens que s'il distingue : une école
  // qui n'ouvre pas ces dossiers (aucun élève n'en a, cas du CEPE de Daniel en
  // 2026) ne doit ni voir « Pas de dossier » partout, ni avoir une case
  // « liste complète » qui ne marque personne. Tous comptent alors comme présentés.
  const dossiersParlants = useMemo(() => {
    const eleves = analyse.candidats.filter((c) => c.type === 'eleve');
    const avecDossier = eleves.filter((c) => c.inscrit).length;
    return avecDossier > 0 && avecDossier < eleves.length;
  }, [analyse]);
  const presente = useCallback((c) => !dossiersParlants || c.inscrit, [dossiersParlants]);

  // Nos élèves sans résultat qu'aucune ligne ne rapproche : les inscrits d'abord.
  const sansLigne = useMemo(
    () => analyse.candidats
      .filter((c) => !pris.has(c.cle) && !enAttente.has(c.cle) && !clesDeja.has(c.cle) && !c.resultat_actuel)
      .sort((a, b) => Number(b.inscrit) - Number(a.inscrit)),
    [analyse, pris, enAttente, clesDeja]
  );
  const lignesSeules = useMemo(
    () => lignesSansEleve.filter((l) => !liens[l.ligne] && !nouveauxLibres[l.ligne]),
    [lignesSansEleve, liens, nouveauxLibres]
  );

  // Le pointage par paires (parti B, GO de Daniel du 01/11/2026). Les pistes
  // vont dans les deux sens - de notre élève vers la ligne qui lui ressemble,
  // et de la ligne vers notre élève - et montraient deux fois la même paire
  // (KOKOLO Mignon Stone ↔ MBAMA Lovi Presley à gauche ET à droite). Chaque
  // paire n'existe plus qu'une fois, notre élève face à la ligne, jusqu'à ce
  // que la personne tranche : « C'est lui » les relie, « Ce n'est pas lui »
  // les renvoie chacun dans sa liste sans piste.
  const paires = useMemo(() => {
    const eleves = new Map(sansLigne.map((c) => [c.cle, c]));
    const lignes = new Map(lignesSeules.map((l) => [l.ligne, l]));
    const parCleLigne = new Map();
    const ajouter = (cle, ligne, piste) => {
      const id = `${cle}|${ligne}`;
      if (ecartees.has(id) || !eleves.has(cle) || !lignes.has(ligne)) return;
      const actuelle = parCleLigne.get(id);
      if (!actuelle || piste.score > actuelle.score) {
        parCleLigne.set(id, { id, eleve: eleves.get(cle), ligne: lignes.get(ligne), motif: piste.motif, score: piste.score });
      }
    };
    for (const c of sansLigne) {
      const piste = pistesCandidats[c.cle];
      if (piste) ajouter(c.cle, piste.ligne, piste);
    }
    for (const l of lignesSeules) {
      if (l.piste) ajouter(l.piste.cle, l.ligne, l.piste);
    }
    // Une paire pour une : chaque élève et chaque ligne au plus une fois, les
    // plus fortes ressemblances d'abord. Sans cela, l'élève qui était la
    // meilleure piste de plusieurs lignes revenait sur chacune (MAHOUNGOU
    // Dieuveil trois fois, WADA KOUTSIA Mavie deux fois, recette du
    // 01/11/2026). Une paire écartée libère l'élève pour la ligne suivante.
    const elevesPris = new Set();
    const lignesPrises = new Set();
    const retenues = [];
    for (const paire of [...parCleLigne.values()].sort((a, b) => b.score - a.score)) {
      if (elevesPris.has(paire.eleve.cle) || lignesPrises.has(paire.ligne.ligne)) continue;
      elevesPris.add(paire.eleve.cle);
      lignesPrises.add(paire.ligne.ligne);
      retenues.push(paire);
    }
    return retenues;
  }, [sansLigne, lignesSeules, pistesCandidats, ecartees]);

  const clesEnPaire = useMemo(() => new Set(paires.map((p) => p.eleve.cle)), [paires]);
  const lignesEnPaire = useMemo(() => new Set(paires.map((p) => p.ligne.ligne)), [paires]);
  // Ce qui n'a vraiment aucune piste : seul ici se marque ajourné ou absent, ou
  // se déclare candidat libre. Un élève encore dans une paire n'est jamais
  // marqué d'office - comme un « à confirmer » non tranché.
  const elevesSansPiste = useMemo(() => sansLigne.filter((c) => !clesEnPaire.has(c.cle)), [sansLigne, clesEnPaire]);
  const lignesSansPiste = useMemo(
    () => lignesSeules.filter((l) => !lignesEnPaire.has(l.ligne)),
    [lignesSeules, lignesEnPaire]
  );

  const ecarterPaire = (id) => setEcartees((precedent) => new Set([...precedent, id]));

  const resultatParDefaut = (fichier) => fichier?.resultat || (typeListe === 'admis' ? 'admis' : null);

  const relier = (ligne, cle) => {
    if (liens[ligne] || nouveauxLibres[ligne] || pris.has(cle)) return;
    setLiens((precedents) => ({
      ...precedents,
      [ligne]: { cle, resultat: resultatParDefaut(fichiersSeuls.get(ligne)) }
    }));
  };
  const defaire = (ligne) => setLiens((precedents) => {
    const suivants = { ...precedents };
    delete suivants[ligne];
    return suivants;
  });

  const declarerLibre = (ligne, formulaire) => {
    setNouveauxLibres((precedents) => ({
      ...precedents,
      [ligne]: { ...formulaire, resultat: precedents[ligne]?.resultat || resultatParDefaut(fichiersSeuls.get(ligne)) }
    }));
    setDeclaration(null);
  };
  const annulerLibre = (ligne) => setNouveauxLibres((precedents) => {
    const suivants = { ...precedents };
    delete suivants[ligne];
    return suivants;
  });

  const marquer = (cle, valeur) => {
    setMarques((precedentes) => {
      const suivantes = { ...precedentes };
      if (valeur) suivantes[cle] = valeur;
      else delete suivantes[cle];
      return suivantes;
    });
    setMarquesAuto((precedent) => {
      const suivant = new Set(precedent);
      suivant.delete(cle);
      return suivant;
    });
  };

  // « La liste est complète » : un raccourci qui marque ajournés, d'un coup,
  // nos inscrits sans ligne pas encore marqués. Décoché, il retire seulement
  // ce qu'il a posé - les marques faites à la main restent.
  const basculerListeComplete = (coche) => {
    setListeComplete(coche);
    if (coche) {
      const aMarquer = elevesSansPiste.filter((c) => presente(c) && !marques[c.cle]).map((c) => c.cle);
      setMarques((precedentes) => ({ ...precedentes, ...Object.fromEntries(aMarquer.map((cle) => [cle, 'ajourne'])) }));
      setMarquesAuto(new Set(aMarquer));
    } else {
      setMarques((precedentes) => Object.fromEntries(
        Object.entries(precedentes).filter(([cle]) => !marquesAuto.has(cle))
      ));
      setMarquesAuto(new Set());
    }
  };

  // Les « à confirmer » dont l'élève proposé est né le même jour que la ligne,
  // pas encore retenus, et dont l'élève n'est pas déjà pris ailleurs. Les
  // retenir reste un geste de la personne : un bouton, pas une coche d'office.
  const memeDate = useMemo(() => aConfirmer.filter((p) => {
    const d = decisions[p.ligne];
    const candidat = d?.cle ? parCle.get(d.cle) : null;
    return candidat && !d.retenue && !pris.has(d.cle)
      && candidat.date_naissance && candidat.date_naissance === p.fichier.date_naissance;
  }), [aConfirmer, decisions, pris, parCle]);

  const retenirMemeDate = () => setDecisions((precedentes) => {
    const suivantes = { ...precedentes };
    const dejaRetenus = new Set();
    for (const p of memeDate) {
      // Deux lignes vers le même élève : seule la première est retenue.
      if (dejaRetenus.has(suivantes[p.ligne].cle)) continue;
      dejaRetenus.add(suivantes[p.ligne].cle);
      suivantes[p.ligne] = { ...suivantes[p.ligne], retenue: true };
    }
    return suivantes;
  });

  // « Tout retenir » : sur une grande liste, cocher les « à confirmer » un par
  // un est fastidieux. Le geste reste celui de la personne, après une
  // confirmation qui dit combien ont une date de naissance différente. Un
  // élève déjà attribué, ou proposé sur deux lignes, n'est retenu qu'une fois.
  const aRetenirTout = useMemo(() => {
    const dejaRetenus = new Set(pris);
    return aConfirmer.filter((p) => {
      const d = decisions[p.ligne];
      if (!d?.cle || d.retenue || dejaRetenus.has(d.cle)) return false;
      dejaRetenus.add(d.cle);
      return true;
    });
  }, [aConfirmer, decisions, pris]);
  const aRetenirToutDateDifferente = aRetenirTout.filter((p) => {
    const candidat = parCle.get(decisions[p.ligne].cle);
    return candidat?.date_naissance && p.fichier.date_naissance && candidat.date_naissance !== p.fichier.date_naissance;
  }).length;
  const retenirTout = () => {
    const lignes = new Set(aRetenirTout.map((p) => p.ligne));
    setDecisions((precedentes) => Object.fromEntries(Object.entries(precedentes).map(([ligne, d]) => [
      ligne,
      lignes.has(Number(ligne)) ? { ...d, retenue: true } : d
    ])));
    setConfirmerToutRetenir(false);
  };
  const nombreRetenusAConfirmer = aConfirmer.filter((p) => decisions[p.ligne]?.retenue).length;
  const decocherTout = () => setDecisions((precedentes) => {
    const suivantes = { ...precedentes };
    for (const p of aConfirmer) suivantes[p.ligne] = { ...suivantes[p.ligne], retenue: false };
    return suivantes;
  });

  const sansResultat = retenues.filter((p) => !decisions[p.ligne].resultat).length
    + liensListe.filter((l) => !l.resultat).length
    + nouveauxListe.filter((n) => !n.resultat).length;

  const charge = useMemo(() => [
    ...retenues.map((p) => depuisFichier({ cle: decisions[p.ligne].cle }, decisions[p.ligne].resultat, p.fichier)),
    ...liensListe.map((l) => depuisFichier({ cle: l.cle }, l.resultat, fichiersSeuls.get(l.ligne))),
    ...elevesSansPiste.filter((c) => marques[c.cle]).map((c) => ({ cle: c.cle, resultat: marques[c.cle] }))
  ], [retenues, decisions, liensListe, fichiersSeuls, elevesSansPiste, marques]);

  const chargeLibres = useMemo(() => nouveauxListe.map((n) => depuisFichier({
    nom: n.nom,
    prenom: n.prenom,
    sexe: n.sexe,
    date_naissance: n.date_naissance,
    lieu_naissance: n.lieu_naissance,
    telephone: n.telephone
  }, n.resultat, fichiersSeuls.get(n.ligne))), [nouveauxListe, fichiersSeuls]);

  const aEnregistrer = [...charge, ...chargeLibres];
  const compte = aEnregistrer.reduce((n, r) => ({ ...n, [r.resultat]: (n[r.resultat] || 0) + 1 }), {});
  const bloque = aEnregistrer.length === 0 || doublons.size > 0 || sansResultat > 0;

  // Le bilan, des deux côtés. Chaque élève et chaque ligne est dans un terme
  // et un seul : les égalités tombent juste par construction, et c'est
  // justement ce que la personne doit pouvoir constater.
  const totalEleves = analyse.candidats.length;
  const termesEleves = [
    { libelle: `trouvé${pris.size > 1 ? 's' : ''}`, valeur: pris.size, couleur: COULEURS.trouve },
    { libelle: 'déjà enregistrés', valeur: dejaConnus.length, couleur: COULEURS.deja },
    { libelle: 'à confirmer', valeur: enAttente.size, couleur: COULEURS.attente },
    { libelle: 'en paire à examiner', valeur: clesEnPaire.size, couleur: COULEURS.attente },
    { libelle: 'sans piste', valeur: elevesSansPiste.filter(presente).length, couleur: COULEURS.seul },
    ...(dossiersParlants
      ? [{ libelle: 'sans piste ni dossier d\'examen', valeur: elevesSansPiste.filter((c) => !c.inscrit).length, couleur: COULEURS.horsCompte }]
      : [])
  ];
  const lignesRapprochees = retenues.length + liensListe.length;
  const totalLignes = analyse.lignes_lues + texteIgnore.length;
  const termesLignes = [
    { libelle: `rapprochée${lignesRapprochees > 1 ? 's' : ''}`, valeur: lignesRapprochees, couleur: COULEURS.trouve },
    { libelle: 'déjà enregistrées', valeur: dejaEnregistrees.length, couleur: COULEURS.deja },
    { libelle: 'à confirmer ou écartées', valeur: analyse.propositions.length - retenues.length - dejaEnregistrees.length, couleur: COULEURS.attente },
    { libelle: 'candidats libres déclarés', valeur: nouveauxListe.length, couleur: COULEURS.libre },
    { libelle: 'en paire à examiner', valeur: lignesEnPaire.size, couleur: COULEURS.attente },
    { libelle: 'sans élève chez nous', valeur: analyse.non_trouves.nombre - liensListe.length - nouveauxListe.length - lignesEnPaire.size, couleur: COULEURS.seul },
    { libelle: 'texte du document', valeur: texteIgnore.length + (analyse.lignes_ignorees || 0), couleur: COULEURS.horsCompte }
  ];

  const enregistrer = async () => {
    setEnCours(true);
    try {
      await onConfirmer({
        liste_complete: listeComplete,
        resultats: charge,
        nouveaux_candidats_libres: chargeLibres
      });
    } catch (erreur) {
      notifier(erreur.message || "L'enregistrement a échoué", 'error');
      setEnCours(false);
      setConfirmation(false);
    }
  };

  const optionsCandidat = (p) => [
    ...[p.cle, ...p.alternatives.map((a) => a.cle)]
      .filter((cle, i, liste) => liste.indexOf(cle) === i)
      .map((cle) => ({ valeur: cle, libelle: `${nomDe(parCle.get(cle))} - ${parCle.get(cle)?.classe_nom || ''}` })),
    { valeur: AUCUN, libelle: 'Aucun de nos candidats' }
  ];

  // Des fonctions de rendu, pas des composants : déclarés ici, ils
  // changeraient d'identité à chaque rendu et une liste déroulante se refermerait.
  const celluleResultat = (p) => {
    const d = decisions[p.ligne];
    if (p.fichier.resultat) {
      return (
        <>
          <BadgeResultat resultat={d.resultat} />
          <Remplacement candidat={parCle.get(d.cle)} resultat={d.resultat} retenue={d.retenue} />
        </>
      );
    }
    // Résultat illisible dans le fichier : à choisir avant d'enregistrer.
    return (
      <div className="w-36">
        <ChampSelect
          valeur={d.resultat || undefined}
          onChange={(valeur) => changer(p.ligne, { resultat: valeur })}
          options={OPTIONS_RESULTAT}
          placeholder={p.fichier.resultat_brut ? `« ${p.fichier.resultat_brut} » ?` : 'Résultat ?'}
          className="w-full"
        />
      </div>
    );
  };

  const choixResultat = (valeur, onChange) => (
    <div className="mx-auto w-36">
      <ChampSelect valeur={valeur || undefined} onChange={onChange} options={OPTIONS_RESULTAT} placeholder="Résultat ?" className="w-full" />
    </div>
  );

  const ligneDoublon = (cle) => doublons.has(cle) && (
    <p className="mt-1 text-[11px] text-red-700">Déjà retenu sur une autre ligne</p>
  );

  return (
    <div>
      {controle && <ControleLecture controle={controle} className="mb-4" />}

      <div className="mb-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Bilan titre={`Nos ${totalEleves} élèves candidats`} termes={termesEleves} total={totalEleves} />
        <Bilan titre={`Les ${totalLignes} lignes du document`} termes={termesLignes} total={totalLignes} />
      </div>

      {analyse.doublons_matricule.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          Le fichier porte plusieurs fois le même matricule national : {analyse.doublons_matricule.join(', ')}.
          Ne retenez qu&apos;une ligne pour chacun.
        </div>
      )}

      {trouves.length + aConfirmer.length + dejaEnregistrees.length + paires.length === 0 && liensListe.length === 0 && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <p className="font-semibold">Aucune ligne de la liste n&apos;a été reconnue.</p>
          <p className="mt-1">
            Revenez à l&apos;étape Colonnes et regardez l&apos;aperçu : les colonnes Nom et Prénom(s) doivent contenir
            des noms d&apos;élèves. Vérifiez aussi que la liste est bien celle de l&apos;année affichée dans
            l&apos;en-tête de KLASYS - les résultats de juin 2026 se rapprochent des élèves de 2025-2026.
          </p>
        </div>
      )}

      {aConfirmer.length > 0 && (
        <Section
          titre={`À confirmer (${aConfirmer.length})`}
          ton="attente"
          sousTitre="Le nom ne correspond pas mot pour mot : comparez la ligne de la liste et l'élève proposé, puis cochez. Rien n'est retenu sans coche."
        >
          {memeDate.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-[#eef7fb] px-5 py-3">
              <p className="text-sm text-gray-700">
                <span className="font-semibold text-[#246990]">
                  {memeDate.length} ligne{memeDate.length > 1 ? 's' : ''}
                </span>
                {' '}
                {memeDate.length > 1 ? 'ont' : 'a'} la même date de naissance que l&apos;élève proposé : c&apos;est presque toujours lui.
              </p>
              <Button variant="outline" size="sm" onClick={retenirMemeDate} className={BOUTON_RELIER}>
                Retenir ces {memeDate.length} ligne{memeDate.length > 1 ? 's' : ''}
              </Button>
            </div>
          )}
          {(aRetenirTout.length > 1 || nombreRetenusAConfirmer > 0) && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-gray-50 px-5 py-2">
              <p className="text-xs text-gray-600">
                {nombreRetenusAConfirmer} retenue{nombreRetenusAConfirmer > 1 ? 's' : ''} sur {aConfirmer.length}
              </p>
              <div className="flex gap-2">
                {nombreRetenusAConfirmer > 0 && (
                  <Button variant="outline" size="sm" onClick={decocherTout}>Tout décocher</Button>
                )}
                {aRetenirTout.length > 1 && (
                  <Button variant="outline" size="sm" onClick={() => setConfirmerToutRetenir(true)} className={BOUTON_RELIER}>
                    Tout retenir ({aRetenirTout.length})
                  </Button>
                )}
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <Th>Ligne du fichier</Th>
                  <Th>Candidat proposé</Th>
                  <Th>Pourquoi vérifier</Th>
                  <Th className="text-center">Résultat</Th>
                  <Th className="w-[90px] text-center">Retenir</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {aConfirmer.map((p) => {
                  const d = decisions[p.ligne];
                  const enDouble = d.retenue && doublons.has(d.cle);
                  return (
                    <tr key={p.ligne} className={enDouble ? 'bg-red-50' : 'hover:bg-gray-50'}>
                      <td className="px-4 py-3"><CelluleFichier fichier={p.fichier} /></td>
                      <td className="px-4 py-3">
                        <div className="w-72">
                          <ChampSelect
                            valeur={d.cle || AUCUN}
                            onChange={(valeur) => changer(p.ligne, valeur === AUCUN
                              ? { cle: null, retenue: false }
                              : { cle: valeur })}
                            options={optionsCandidat(p)}
                            className="w-full"
                          />
                        </div>
                        <ComparaisonDate fichier={p.fichier} candidat={parCle.get(d.cle)} />
                        {d.retenue && ligneDoublon(d.cle)}
                      </td>
                      <td className="px-4 py-3 text-xs text-amber-800">{p.motif}</td>
                      <td className="px-4 py-3 text-center">{celluleResultat(p)}</td>
                      <td className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          className={ClasseCase}
                          checked={Boolean(d.retenue)}
                          disabled={!d.cle}
                          onChange={(e) => changer(p.ligne, { retenue: e.target.checked })}
                          aria-label={`Retenir la ligne ${p.fichier.ligne_fichier}`}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {trouves.length > 0 && (
        <Section
          titre={`Trouvés (${trouves.length})`}
          ton="trouve"
          sousTitre="Nom identique ou matricule national déjà connu. Retenus d'office, sauf un élève qui a déjà un autre résultat : cochez pour le remplacer."
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <Th>Ligne du fichier</Th>
                  <Th>Notre candidat</Th>
                  <Th className="text-center">Résultat</Th>
                  <Th className="text-center">Mention</Th>
                  <Th className="w-[90px] text-center">Retenir</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {trouves.map((p) => {
                  const d = decisions[p.ligne];
                  const enDouble = d.retenue && doublons.has(d.cle);
                  return (
                    <tr key={p.ligne} className={enDouble ? 'bg-red-50' : 'hover:bg-gray-50'}>
                      <td className="px-4 py-3"><CelluleFichier fichier={p.fichier} /></td>
                      <td className="px-4 py-3">
                        <CelluleCandidat candidat={parCle.get(d.cle)} />
                        {d.retenue && ligneDoublon(d.cle)}
                      </td>
                      <td className="px-4 py-3 text-center">{celluleResultat(p)}</td>
                      <td className="px-4 py-3 text-center text-sm">
                        {d.resultat === 'admis' && libelleMention(p.fichier.mention)
                          ? libelleMention(p.fichier.mention)
                          : <span className="text-gray-400">-</span>}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          className={ClasseCase}
                          checked={Boolean(d.retenue)}
                          onChange={(e) => changer(p.ligne, { retenue: e.target.checked })}
                          aria-label={`Retenir la ligne ${p.fichier.ligne_fichier}`}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {liensListe.length > 0 && (
        <Section
          titre={`Reliés à la main (${liensListe.length})`}
          ton="trouve"
          sousTitre="Les « C'est lui » que vous avez faits. Défaire remet l'élève et la ligne dans les colonnes à vérifier."
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <Th>Ligne du fichier</Th>
                  <Th>Notre élève</Th>
                  <Th className="text-center">Résultat</Th>
                  <Th className="w-[110px] text-center">Action</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {liensListe.map((l) => {
                  const fichier = fichiersSeuls.get(l.ligne);
                  return (
                    <tr key={l.ligne} className={doublons.has(l.cle) ? 'bg-red-50' : 'hover:bg-gray-50'}>
                      <td className="px-4 py-3">{fichier && <CelluleFichier fichier={fichier} />}</td>
                      <td className="px-4 py-3">
                        <CelluleCandidat candidat={parCle.get(l.cle)} />
                        {ligneDoublon(l.cle)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {fichier?.resultat || typeListe === 'admis'
                          ? <BadgeResultat resultat={l.resultat} />
                          : choixResultat(l.resultat, (valeur) => setLiens((precedents) => ({
                            ...precedents,
                            [l.ligne]: { ...precedents[l.ligne], resultat: valeur }
                          })))}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Button variant="outline" size="sm" onClick={() => defaire(l.ligne)}>Défaire</Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {nouveauxListe.length > 0 && (
        <Section
          titre={`Nouveaux candidats libres (${nouveauxListe.length})`}
          ton="libre"
          sousTitre="Créés à l'enregistrement, avec leur résultat et sans frais d'examen à payer. Comptés à part, hors du taux de réussite de l'école."
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <Th>Ligne du fichier</Th>
                  <Th>Candidat libre</Th>
                  <Th className="text-center">Résultat</Th>
                  <Th className="w-[200px] text-center">Action</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {nouveauxListe.map((n) => {
                  const fichier = fichiersSeuls.get(n.ligne);
                  return (
                    <tr key={n.ligne} className="hover:bg-gray-50">
                      <td className="px-4 py-3">{fichier && <CelluleFichier fichier={fichier} />}</td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900">{n.nom} {n.prenom}</p>
                        <p className="text-[11px] text-gray-500">
                          {[n.sexe === 'F' ? 'Féminin' : 'Masculin', `Né(e) le ${formaterDate(n.date_naissance)}`, n.lieu_naissance]
                            .filter(Boolean).join(' - ')}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {fichier?.resultat || typeListe === 'admis'
                          ? <BadgeResultat resultat={n.resultat} />
                          : choixResultat(n.resultat, (valeur) => setNouveauxLibres((precedents) => ({
                            ...precedents,
                            [n.ligne]: { ...precedents[n.ligne], resultat: valeur }
                          })))}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex justify-center gap-2">
                          <Button variant="outline" size="sm" onClick={() => setDeclaration({ ligne: n.ligne })}>Modifier</Button>
                          <Button variant="outline" size="sm" onClick={() => annulerLibre(n.ligne)}>Annuler</Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {dejaConnus.length > 0 && (
        <Section
          titre={`Déjà enregistrés (${dejaConnus.length})`}
          ton="deja"
          sousTitre="Ces élèves ont déjà un résultat (import précédent ou saisie à la main) : cet import n'y touche pas. Pour en changer un, passez par la vue Résultats."
        >
          <div className="px-5 py-3">
            <p className="text-sm text-gray-700">
              {dejaEnregistrees.length > 0 && (
                <>
                  <span className="font-semibold">{dejaEnregistrees.length}</span> figure{dejaEnregistrees.length > 1 ? 'nt' : ''} dans
                  ce fichier avec le même résultat : rien à refaire.{' '}
                </>
              )}
              {dejaAbsents.length > 0 && (
                <>
                  <span className="font-semibold">{dejaAbsents.length}</span> n&apos;{dejaAbsents.length > 1 ? 'y figurent' : 'y figure'} pas
                  (une autre page de la liste, ou une saisie à la main) : ni proposé{dejaAbsents.length > 1 ? 's' : ''} ajourné{dejaAbsents.length > 1 ? 's' : ''}, ni modifié{dejaAbsents.length > 1 ? 's' : ''}.
                </>
              )}
            </p>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => setVoirDeja((v) => !v)}>
              {voirDeja ? 'Masquer la liste' : 'Voir la liste'}
            </Button>
            {voirDeja && (
              <ul className="mt-3 divide-y divide-gray-100 rounded-lg border border-gray-200">
                {dejaConnus.map((c) => (
                  <li key={c.cle} className="flex items-center justify-between gap-3 px-4 py-2">
                    <CelluleCandidat candidat={c} />
                    <div className="flex items-center gap-3">
                      <span className="text-[11px] text-gray-500">{clesDeja.has(c.cle) ? 'dans ce fichier' : 'absent de ce fichier'}</span>
                      <BadgeResultat resultat={c.resultat_actuel} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Section>
      )}

      {paires.length > 0 && (
        <Section
          ton="attente"
          titre={`Pistes à examiner (${paires.length})`}
          sousTitre="Notre élève, face à la ligne de la liste qui lui ressemble le plus. « C'est lui » les relie ; « Ce n'est pas lui » les renvoie chacun dans sa liste sans piste, plus bas."
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <Th>Notre élève</Th>
                  {dossiersParlants && <Th className="w-[140px] text-center">Dossier d&apos;examen</Th>}
                  <Th>Sur la liste</Th>
                  <Th>Ressemblance</Th>
                  <Th className="w-[250px] text-center">Décision</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {paires.map((p) => {
                  const memeJour = /même date/i.test(p.motif);
                  return (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900">{nomDe(p.eleve)}</p>
                        <p className="text-[11px] text-gray-500">
                          {[p.eleve.classe_nom, p.eleve.date_naissance && `Né(e) le ${formaterDate(p.eleve.date_naissance)}`].filter(Boolean).join(' - ')}
                        </p>
                      </td>
                      {dossiersParlants && (
                        <td className="px-4 py-3 text-center"><BadgeDossier inscrit={p.eleve.inscrit} /></td>
                      )}
                      <td className="px-4 py-3"><CelluleFichier fichier={p.ligne.fichier} /></td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-lg px-2 py-1 text-[11px] font-medium ${memeJour
                          ? 'bg-green-50 text-green-800'
                          : 'bg-amber-50 text-amber-800'
                          }`}
                        >
                          {p.motif}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-center gap-2">
                          <Button variant="outline" size="sm" onClick={() => ecarterPaire(p.id)} className="h-8">
                            Ce n&apos;est pas lui
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => relier(p.ligne.ligne, p.eleve.cle)} className={BOUTON_RELIER}>
                            C&apos;est lui
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {(elevesSansPiste.length > 0 || lignesSansPiste.length > 0) && (
        <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Section
            ton="seul"
            className=""
            titre={`Nos élèves sans aucune piste (${elevesSansPiste.length})`}
            sousTitre={`Aucune ligne de la liste ne leur ressemble. Marquez-les ajournés, ou absents s'ils ne se sont pas présentés ; sans marque, le résultat reste vide.${dossiersParlants
              ? " « Sans dossier » : aucun dossier d'inscription à cet examen dans KLASYS (vue Dossiers)."
              : ''}`}
          >
            {typeListe === 'admis' && elevesSansPiste.some(presente) && (
              <label className="flex cursor-pointer items-start gap-3 border-b bg-[#eef7fb] px-4 py-3 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className={`${ClasseCase} mt-0.5`}
                  checked={listeComplete}
                  onChange={(e) => basculerListeComplete(e.target.checked)}
                />
                <span>
                  <span className="font-semibold text-[#246990]">La liste est complète pour notre école</span>
                  <span className="block text-xs text-gray-500">
                    {dossiersParlants
                      ? "Marque ajournés d'un coup ceux qui ont un dossier d'examen. Les autres n'ont peut-être pas été présentés."
                      : "Marque ajournés d'un coup tous nos élèves sans piste."}
                  </span>
                </span>
              </label>
            )}
            <div className="max-h-[480px] divide-y divide-gray-100 overflow-auto">
              {elevesSansPiste.length === 0 && (
                <p className="px-4 py-4 text-sm text-gray-500">Chacun de nos élèves restants a une piste à examiner.</p>
              )}
              {elevesSansPiste.map((c) => (
                <div
                  key={c.cle}
                  className={`grid items-center gap-3 px-4 py-3 ${dossiersParlants
                    ? 'grid-cols-[minmax(0,1fr)_110px_auto]'
                    : 'grid-cols-[minmax(0,1fr)_auto]'
                    }`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900">{nomDe(c)}</p>
                    <p className="text-[11px] text-gray-500">
                      {[c.classe_nom, c.date_naissance && `Né(e) le ${formaterDate(c.date_naissance)}`].filter(Boolean).join(' - ')}
                    </p>
                  </div>
                  {dossiersParlants && <BadgeDossier inscrit={c.inscrit} />}
                  <MarqueSansLigne valeur={marques[c.cle] || null} onChoisir={(valeur) => marquer(c.cle, valeur)} nom={nomDe(c)} />
                </div>
              ))}
            </div>
          </Section>

          <Section
            ton="seul"
            className=""
            titre={`Élèves de la liste sans aucune piste (${analyse.non_trouves.nombre - liensListe.length - nouveauxListe.length - lignesEnPaire.size})`}
            sousTitre="Sur la liste, et aucun de nos élèves ne leur ressemble. Un élève de l'école absent de KLASYS peut être déclaré candidat libre."
          >
            <div className="max-h-[480px] divide-y divide-gray-100 overflow-auto">
              {lignesSansPiste.length === 0 && (
                <p className="px-4 py-4 text-sm text-gray-500">
                  Tous les élèves de la liste ont été rapprochés ou ont une piste. Pour la liste d&apos;une seule école, c&apos;est le résultat attendu.
                </p>
              )}
              {lignesSansPiste.map((l) => {
                // L'élève le plus proche est déjà pris : relié, ou en paire plus
                // haut avec une ligne qui lui ressemble davantage.
                // Pas de rappel pour une paire que la personne a écartée : elle a
                // déjà dit que ce n'était pas lui.
                const procheCle = l.piste?.cle;
                const ressemble = procheCle && !ecartees.has(`${procheCle}|${l.ligne}`)
                  && (pris.has(procheCle) || clesEnPaire.has(procheCle)) ? parCle.get(procheCle) : null;
                return (
                  <div key={l.ligne} className="flex items-center justify-between gap-3 px-4 py-3">
                    <div className="min-w-0">
                      <CelluleFichier fichier={l.fichier} />
                      {ressemble && (
                        <p className="mt-0.5 text-[11px] text-gray-500">
                          Ressemble aussi à {nomDe(ressemble)}, {pris.has(procheCle) ? 'déjà attribué à une autre ligne' : 'en paire plus haut'}.
                        </p>
                      )}
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setDeclaration({ ligne: l.ligne })} className="h-8 flex-shrink-0">
                      Candidat libre
                    </Button>
                  </div>
                );
              })}
              {analyse.non_trouves.nombre > lignesSansEleve.length && (
                <p className="px-4 py-3 text-xs text-gray-500">
                  Et {analyse.non_trouves.nombre - lignesSansEleve.length} autres lignes, sans aucune piste chez nous :
                  la liste couvre d&apos;autres écoles.
                </p>
              )}
            </div>
          </Section>
        </div>
      )}

      {texteIgnore.length > 0 && (
        <Section
          titre={`Texte du document mis de côté (${texteIgnore.length})`}
          ton="texte"
          sousTitre="En-têtes, titres et pied de page : des lignes sans élève (ni date de naissance, ni matricule). Elles ne sont pas rapprochées."
        >
          <div className="px-5 py-3">
            <Button variant="outline" size="sm" onClick={() => setVoirTexte((v) => !v)}>
              {voirTexte ? 'Masquer ces lignes' : 'Voir ces lignes'}
            </Button>
            {voirTexte && (
              <ul className="mt-3 space-y-1 text-sm text-gray-700">
                {texteIgnore.slice(0, 200).map((ligne) => (
                  <li key={ligne.ligne}>
                    <span className="text-xs text-gray-400 tabular-nums">Ligne {ligne.ligne}</span> {texteDeLigne(ligne)}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Section>
      )}

      <PiedActions
        gauche={doublons.size > 0
          ? <span className="text-red-700">Un même élève est attribué à deux lignes : décochez ou défaites-en une.</span>
          : sansResultat > 0
            ? <span className="text-red-700">{sansResultat} ligne{sansResultat > 1 ? 's' : ''} retenue{sansResultat > 1 ? 's' : ''} sans résultat lisible : choisissez-le.</span>
            : (
              <span>
                <span className="font-semibold text-gray-900">{aEnregistrer.length} résultat{aEnregistrer.length > 1 ? 's' : ''}</span>
                {' à enregistrer : '}
                {compte.admis || 0} admis, {compte.ajourne || 0} ajourné{(compte.ajourne || 0) > 1 ? 's' : ''}, {compte.absent || 0} absent{(compte.absent || 0) > 1 ? 's' : ''}
                {chargeLibres.length > 0 && `, dont ${chargeLibres.length} nouveau${chargeLibres.length > 1 ? 'x' : ''} candidat${chargeLibres.length > 1 ? 's' : ''} libre${chargeLibres.length > 1 ? 's' : ''}`}
              </span>
            )}
      >
        <Button variant="outline" onClick={onRetour} disabled={enCours} className={BOUTON_PIED}>
          <ArrowLeft size={16} />
          Retour
        </Button>
        <Button
          onClick={() => setConfirmation(true)}
          disabled={bloque || enCours}
          className={`${BOUTON_PIED} bg-[#246990] text-white hover:bg-[#1f5a7a] disabled:opacity-50`}
        >
          {enCours ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
          Enregistrer
        </Button>
      </PiedActions>

      {declaration && (
        <DeclarationCandidatLibre
          key={declaration.ligne}
          fichier={fichiersSeuls.get(declaration.ligne)}
          initial={nouveauxLibres[declaration.ligne] || null}
          libelleLong={libelleLong}
          onFermer={() => setDeclaration(null)}
          onDeclarer={(formulaire) => declarerLibre(declaration.ligne, formulaire)}
        />
      )}

      <ConfirmDialog
        isOpen={confirmerToutRetenir}
        onClose={() => setConfirmerToutRetenir(false)}
        onConfirm={retenirTout}
        type="warning"
        title={`Retenir les ${aRetenirTout.length} lignes à confirmer ?`}
        description={(aRetenirToutDateDifferente > 0
          ? `Dont ${aRetenirToutDateDifferente} avec une date de naissance différente entre KLASYS et la liste. `
          : 'Toutes ont la même date de naissance, ou une date inconnue d\'un côté. ')
          + "Chaque élève proposé sera retenu tel quel ; vous pourrez encore décocher une ligne avant d'enregistrer."}
        confirmText="Tout retenir"
        cancelText="Annuler"
      />

      <ConfirmDialog
        isOpen={confirmation}
        isLoading={enCours}
        onClose={() => !enCours && setConfirmation(false)}
        onConfirm={enregistrer}
        type="info"
        title={`Enregistrer ${aEnregistrer.length} résultat${aEnregistrer.length > 1 ? 's' : ''} au ${libelleLong} ?`}
        description={`${compte.admis || 0} admis, ${compte.ajourne || 0} ajourné(s), ${compte.absent || 0} absent(s)`
          + (chargeLibres.length > 0 ? `, dont ${chargeLibres.length} nouveau(x) candidat(s) libre(s)` : '')
          + ". Les résultats déjà enregistrés ne sont remplacés que là où vous l'avez coché."}
        confirmText="Enregistrer"
        cancelText="Annuler"
      />
    </div>
  );
};

export default RapprochementResultats;
